/**
 * FarmDetailSkeleton Component
 * 
 * Dedicated skeleton component for FarmDetailPage with exact dimension matching.
 * Handles all detail page sections: Gallery, VerifiedBadge, Overview, Tips, Sidebar, etc.
 * 
 * @author FarmCiti Team
 * @version 1.0.0
 */

import React from 'react'
import FarmDetailStyle from '../../assets/stylesheets/farm-detail/FarmDetailPage.module.css'
import GalleryStyle from '../../assets/stylesheets/farm-detail/sections/Gallery.module.css'
import VerifiedBadgeStyle from '../../assets/stylesheets/farm-detail/sections/VerifiedBadge.module.css'
import FarmOverviewStyle from '../../assets/stylesheets/farm-detail/sections/FarmOverview.module.css'
import SideBarStyle from '../../assets/stylesheets/farm-detail/sections/SideBar.module.css'

/**
 * Gallery Skeleton - matches exact gallery grid structure
 * Shows real title and share button, only skeletons for images
 */
function GallerySkeleton({ title }) {
  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: title,
          text: '',
          url: window.location.href
        })
        .then(() => console.log("Shared successfully"))
        .catch((error) => console.log("Error sharing", error));
    } else {
      alert("Sharing is not supported in this browser.");
    }
  };

  return (
    <div className={GalleryStyle["gallery-container"]}>
      <div className={FarmDetailStyle["farmDetail-header"]}>
        {/* Real title - no skeleton */}
        <h2 className={GalleryStyle["gallery-title"]}>{title}</h2>
        {/* Real share button - no skeleton */}
        <button className={FarmDetailStyle["Gallery-save"]} onClick={handleShare}>
          <svg
            width="15"
            height="18"
            viewBox="0 0 19 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              width="16.3334"
              height="17.5001"
              transform="translate(1.41211 2)"
              fill="none"
            />
            <path
              d="M9.57881 3.16667V13.0834M13.0788 5.50001L9.57881 2L6.07879 5.50001M1.41211 11.3334V17.1667C1.41211 17.7856 1.65794 18.3791 2.09553 18.8166C2.53312 19.2542 3.12661 19.5001 3.74545 19.5001H15.4122C16.031 19.5001 16.6245 19.2542 17.0621 18.8166C17.4997 18.3791 17.7455 17.7856 17.7455 17.1667V11.3334"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Share
        </button>
      </div>

      <div className={GalleryStyle["gallery-grid"]}>
        {/* Large image skeleton - exact same structure as real gallery */}
        <div className={GalleryStyle["gallery-item--large"]}>
          <div className={`${GalleryStyle["gallery-item--large"]} ${GalleryStyle["photo-card"]} ${GalleryStyle.skeleton}`} />
        </div>
        {/* Thumbnail skeletons - exact same classes as real gallery */}
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`${GalleryStyle["gallery-thumb"]} ${GalleryStyle[`thumb${i}`]}`}>
            <div className={`${GalleryStyle["gallery-thumb"]} ${GalleryStyle[`thumb${i}`]} ${GalleryStyle["photo-card"]} ${GalleryStyle.skeleton}`} />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * VerifiedBadge Skeleton - matches badge structure
 */
function VerifiedBadgeSkeleton() {
  return (
    <div className={FarmDetailStyle["farmDetail-section"]}>
      <div className={VerifiedBadgeStyle["vb-header"]}>
        {/* Farm name skeleton - uses exact same class as real title */}
        <h1 className={`${VerifiedBadgeStyle["vb-title"]} ${GalleryStyle.skeleton}`}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </h1>
        {/* Address skeleton - uses exact same class as real address */}
        <p className={`${VerifiedBadgeStyle["vb-address"]} ${GalleryStyle.skeleton}`}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </p>
        {/* Rating skeleton - uses exact same class as real rating */}
        <div className={`${VerifiedBadgeStyle["vb-rateLabel"]} ${GalleryStyle.skeleton}`}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </div>
      </div>
      {/* Features grid skeleton */}
      <ul className={VerifiedBadgeStyle["vb-features"]}>
        {Array.from({ length: 8 }).map((_, i) => (
          <li key={i} className={VerifiedBadgeStyle["vb-featureItem"]}>
            <div className={`${VerifiedBadgeStyle["vb-featureIcon"]} ${GalleryStyle.skeleton}`} />
            <span className={`${VerifiedBadgeStyle["vb-featureText"]} ${GalleryStyle.skeleton}`}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Overview Skeleton - matches overview text and contact structure
 */
function OverviewSkeleton() {
  return (
    <div className={FarmDetailStyle["farmDetail-section"]}>
      {/* Overview title skeleton - uses exact same class as real title */}
      <h2 className={`${FarmOverviewStyle["overview-title"]} ${GalleryStyle.skeleton}`}>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      </h2>
      
      {/* Description skeleton - uses exact same classes as real text */}
      <div style={{ padding: 'var(--gap-md)' }}>
        <p className={`${FarmOverviewStyle["overview-text"]} ${GalleryStyle.skeleton}`}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </p>
        <p className={`${FarmOverviewStyle["overview-text"]} ${GalleryStyle.skeleton}`}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </p>
      </div>

      {/* Contact info skeleton - uses exact same classes as real contact info */}
      <div className={FarmOverviewStyle["contact-info"]}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={FarmOverviewStyle["contact-row"]}>
            <div className={`${FarmOverviewStyle["info-icon"]} ${GalleryStyle.skeleton}`} />
            <span className={`${FarmOverviewStyle["contact-text"]} ${GalleryStyle.skeleton}`}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Tips Skeleton - matches tips list structure
 */
function TipsSkeleton() {
  return (
    <div className={FarmDetailStyle["farmDetail-section"]}>
      {/* Tips title skeleton - uses exact same class as real title */}
      <h2 className={`${FarmOverviewStyle["overview-title"]} ${GalleryStyle.skeleton}`}>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      </h2>
      
      {/* Tips list skeleton - uses natural content-based sizing */}
      <div style={{ padding: 'var(--gap-md)' }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ marginBottom: 'var(--gap-md)' }}>
            {/* Tip title - uses content to determine natural size */}
            <h3 className={`${GalleryStyle.skeleton}`} style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--gap-sm)' }}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </h3>
            {/* Tip text - uses natural paragraph sizing */}
            <p className={`${GalleryStyle.skeleton}`}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Sidebar Skeleton - matches sidebar info structure
 */
function SidebarSkeleton() {
  return (
    <div className={FarmDetailStyle["detail-sidebar"]}>
      <div className={SideBarStyle["sidebar-container"]}>
        {/* Hours section skeleton - using natural content sizing */}
        <div style={{ marginBottom: 'var(--gap-lg)' }}>
          {/* Section title - using h3 with natural sizing */}
          <h3 className={`${GalleryStyle.skeleton}`} style={{ marginBottom: 'var(--gap-sm)' }}>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </h3>
          {/* Hours list skeleton - using proper semantic structure */}
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: 'var(--gap-xs)' 
            }}>
              {/* Day name */}
              <span className={`${GalleryStyle.skeleton}`}>
                &nbsp;&nbsp;&nbsp;&nbsp;
              </span>
              {/* Hours */}
              <span className={`${GalleryStyle.skeleton}`}>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Main FarmDetailSkeleton component
 */
export default function FarmDetailSkeleton() {
  return (
    <main className={FarmDetailStyle["detail-main-content"]}>
      <section className={FarmDetailStyle["detail-overview"]}>
        {/* Gallery Skeleton */}
        <GallerySkeleton title="Farm Gallery" />

        <div className={FarmDetailStyle["detail-container"]}>
          <div className={FarmDetailStyle["detail-main"]}>
            {/* VerifiedBadge Skeleton */}
            <VerifiedBadgeSkeleton />
            
            {/* Overview Skeleton */}
            <OverviewSkeleton />
            
            {/* Tips Skeleton */}
            <TipsSkeleton />
          </div>

          {/* Sidebar Skeleton */}
          <SidebarSkeleton />
        </div>
      </section>

      {/* Map Section Skeleton */}
      <section className={FarmDetailStyle["section"]}>
        <div 
          className={`${GalleryStyle.skeleton}`}
          style={{ width: '100%', height: '40vh', borderRadius: 'var(--border-radius-sm)' }}
        />
      </section>

      {/* Banner Section Skeleton */}
      <section className={`${FarmDetailStyle["section"]} ${FarmDetailStyle["banner-section"]}`}>
        <div className={FarmDetailStyle["middle-banner"]}>
          <div 
            className={`${GalleryStyle.skeleton}`}
            style={{ width: '100%', height: '200px', borderRadius: 'var(--border-radius-sm)' }}
          />
        </div>
      </section>
    </main>
  )
}

// Export individual skeleton components for modular use
export {
  GallerySkeleton,
  VerifiedBadgeSkeleton, 
  OverviewSkeleton,
  TipsSkeleton,
  SidebarSkeleton
}
